import {FC, useState, useEffect} from 'react'
import {useLocation} from 'react-router-dom'
import {useDispatch} from 'react-redux'
import queryString from 'query-string'
import {isApolloError, useApolloClient} from '@apollo/client'
import Loading from 'components/Loading'
import {HEALTHCARE_STATUS, HealthcareStatusQueryResult} from 'features/healthcare/HealthcareStartModal'
import {useBroadcastReloadHealthcareStatus} from 'features/healthcare/useBroadcastReloadHealthcareStatus'
import {useSelector} from 'hooks/redux'
import {handleQueryError} from 'libs/graphql'
import {isFullAdminSelector} from 'libs/redux'
import {asyncError} from 'store/actions/asyncError'
import {
  REGISTER_HEALTHCARE,
  RegisterHealthcareQueryResult,
  RegisterHealthcareQueryVariables,
  START_HEALTHCARE,
  StartHealthcareQueryResult,
} from './query'
import styles from './HealthcareStart.scss'

interface SearchParams {
  stress_check?: boolean
  stress_check_role?: string
  regular_health_checkup?: boolean
}

export const Index: FC = () => {
  const dispatch = useDispatch()
  const isFullAdmin = useSelector(isFullAdminSelector)

  const {search} = useLocation()
  const params = queryString.parse(search, {parseBooleans: true}) as SearchParams

  const apolloClient = useApolloClient()
  const [error, setError] = useState(false)

  const broadcastReloadHealthcareStatus = useBroadcastReloadHealthcareStatus()

  useEffect(() => {
    const isUnavailable = async () => {
      const res = await apolloClient.query<HealthcareStatusQueryResult>({
        query: HEALTHCARE_STATUS,
        fetchPolicy: 'no-cache',
      })
      if (res.data.healthcareStatus === 'NOT_STARTED' && isFullAdmin) {
        return false
      } else if (res.data.healthcareStatus === 'HAS_ADMIN') {
        return false
      } else {
        return true
      }
    }

    const register = async () => {
      const res = await apolloClient.mutate<RegisterHealthcareQueryResult, RegisterHealthcareQueryVariables>({
        mutation: REGISTER_HEALTHCARE,
        variables: {
          input: {
            stressCheck: params.stress_check,
            stressCheckRole: params.stress_check_role,
            regularHealthCheckup: params.regular_health_checkup,
          },
        },
      })
      if (res.data?.registerHealthcare?.status === 'started' && res.data.registerHealthcare.link) {
        broadcastReloadHealthcareStatus()
        window.location.href = res.data.registerHealthcare.link
      }
    }

    const start = async () => {
      try {
        if (await isUnavailable()) {
          setError(true)
          return
        }

        const res = await apolloClient.mutate<StartHealthcareQueryResult>({
          mutation: START_HEALTHCARE,
        })
        if (res.data?.startHealthcare?.status === 'started' && res.data.startHealthcare.link) {
          window.location.href = res.data.startHealthcare.link
        } else {
          await register()
        }
      } catch (err) {
        if (isApolloError(err)) {
          handleQueryError(err, dispatch)
        } else {
          dispatch(asyncError(err))
        }
        setError(true)
      }
    }

    start()
  }, [
    dispatch,
    isFullAdmin,
    apolloClient,
    broadcastReloadHealthcareStatus,
    params.stress_check,
    params.stress_check_role,
    params.regular_health_checkup,
  ])

  return (
    <div className="l-wrap-xs u-pt100">
      {error ? (
        <div className="l-box-message">エラーが発生しました</div>
      ) : (
        <div className={styles.loading}>
          <Loading />
        </div>
      )}
    </div>
  )
}
